import * as React from 'react';
import { Button } from 'react-bootstrap';
import { IWatrRequest } from '@nkr/models/index';
import { bankingDisplay, showUsAchBanking } from './banking-mask';
import './compact-banking-row.css';

export { showUsAchBanking };

const formatBankAddress = (data: IWatrRequest): string => {
    const bi = data.bankInformation;

    if (!bi) {
        return '';
    }
    return [bi.cityTownLocality, bi.stateProvinceRegion, bi.postalCode]
        .map(v => (v || '').trim())
        .filter(v => v !== '')
        .join(', ');
};

const Pair = (props: {
    label: string;
    value: string;
    title?: string;
    mono?: boolean;
    className?: string;
}) => {
    const empty = !props.value || props.value === '—';
    const pairClass = ['compact-banking__pair', props.className || ''].filter(Boolean).join(' ');
    const valueClass = [
        'compact-banking__value',
        props.mono ? 'compact-banking__value--mono' : '',
        empty ? 'compact-banking__value--muted' : ''
    ].filter(Boolean).join(' ');

    return (
        <span className={pairClass}>
            <span className="compact-banking__label">{props.label}</span>
            <span className={valueClass} title={props.title}>{props.value || '—'}</span>
        </span>
    );
};

interface ICompactBankingRowProps {
    data: IWatrRequest;
    bankingRevealed: boolean;
    onToggleBankingRevealed?: () => void;
    variant?: 'plain' | 'boxed';
    showPaymentLabel?: boolean;
}

export const CompactBankingRow: React.FC<ICompactBankingRowProps> = ({
    data,
    bankingRevealed,
    onToggleBankingRevealed,
    variant = 'plain',
    showPaymentLabel = true
}) => {
    const isUsAch = showUsAchBanking(data);
    const maskedTitle = bankingRevealed ? undefined : 'Masked for security; use Show to reveal.';
    const bankName = (data.bankInformation?.nameOfBank || '').trim();
    const bankAddress = formatBankAddress(data);
    const additionalCode = data.bankInformation?.additionalCode;
    const rootClass = [
        'compact-banking',
        'wage-summary-banking-row',
        variant === 'plain' ? 'compact-banking--plain' : '',
        isUsAch ? '' : 'compact-banking--intl'
    ].filter(Boolean).join(' ');

    const toggle = onToggleBankingRevealed ? (
        <Button
            bsStyle="link"
            className="compact-banking__toggle"
            onClick={onToggleBankingRevealed}
        >
            {bankingRevealed ? 'Hide' : 'Show'}
        </Button>
    ) : null;

    return (
        <div className={rootClass}>
            <h4 className="compact-banking__title">{showPaymentLabel ? 'Payment' : 'Banking'}</h4>

            {isUsAch ? (
                <div className="compact-banking__codes">
                    <Pair
                        label="Routing"
                        value={bankingDisplay(data.achRoutingNumber, bankingRevealed, 4)}
                        title={maskedTitle}
                        mono
                        className="compact-banking__pair--code"
                    />
                    <Pair
                        label="Account"
                        value={bankingDisplay(data.achAccountNumber, bankingRevealed, 4)}
                        title={maskedTitle}
                        mono
                        className="compact-banking__pair--code"
                    />
                </div>
            ) : (
                <div className="compact-banking__identity">
                    <Pair
                        label="Bank name"
                        value={bankName !== '' ? bankName : '—'}
                    />
                    <Pair
                        label="Bank address"
                        value={bankAddress !== '' ? bankAddress : '—'}
                    />
                </div>
            )}

            {toggle}

            {!isUsAch && (
                <div className="compact-banking__codes compact-banking__codes--indented">
                    <Pair
                        label="IBAN"
                        value={bankingDisplay(additionalCode, bankingRevealed, 4)}
                        title={maskedTitle}
                        mono
                        className="compact-banking__pair--code"
                    />
                    <Pair
                        label="Account"
                        value={bankingDisplay(data.intBankingNumber, bankingRevealed, 4)}
                        title={maskedTitle}
                        mono
                        className="compact-banking__pair--code"
                    />
                    <Pair
                        label="SWIFT"
                        value={bankingDisplay(data.intSwiftNumber, bankingRevealed, 4)}
                        title={maskedTitle}
                        mono
                        className="compact-banking__pair--code"
                    />
                </div>
            )}
        </div>
    );
};

export default CompactBankingRow;
